<template>
	<li ref="document-upload-list-item" class="border-b last:border-0">
		<div
			class="flex items-center justify-between relative gap-2 py-2"
			:class="{
				'uploading-files outline-dashed outline-green-500 rounded-lg -outline-offset-2 z-10 bg-gray-50':
					isOverDropZone,
			}"
		>
			<div class="flex flex-auto gap-4">
				<div
					v-if="fileBlob"
					class="self-center flex justify-center items-center basis-[4rem] min-h-16 shrink-0 grow-0"
				>
					<FilePreviewFileBox
						:file-blob="fileBlob"
						mode="thumbnail"
						class="h-full w-full flex justify-center items-center"
					/>
				</div>
				<AppButton
					v-else-if="!doc.linkedDocumentAction"
					variant="ghost"
					icon="upload"
					class="flex self-center basis-[4rem] h-16 shrink-0 grow-0 border border-dashed hover:border-gray-400 text-gray-400 hover:text-gray-500"
					:class="{
						'border-green-500 bg-green-50 text-green-600': isOverDropZone,
					}"
					:disabled="doc.sentDocumentAction !== null"
					aria-label="Bestand uploaden"
					@click="showSelectFileModal = true"
				/>
				<div
					v-else-if="linkedDoc"
					class="flex justify-center items-center basis-[4rem] h-16 shrink-0 grow-0 overflow-hidden border rounded-sm bg-gray-50"
				>
					<img
						:src="getFileIcon(getFileType(linkedDoc.contentType))"
						class="w-9 h-9"
						:alt="'ContentType is ' + linkedDoc.contentType"
					/>
				</div>

				<div class="flex flex-col">
					<span>{{ splitHdnValue(doc.documentType)[1] }}</span>
					<span class="text-sm text-gray-400 max-w-xl">
						{{ doc.documentTypeDescription }}
					</span>
				</div>
			</div>
			<div class="flex items-center gap-2">
				<AppButton
					icon="docs"
					variant="secondary"
					:disabled="
						Boolean(doc.sentDocumentAction || doc.sentDocumentActionId)
					"
					@click="showSelectFileModal = true"
				>
					Bestand koppelen
				</AppButton>
			</div>
		</div>
		<SelectFileModal
			v-model="showSelectFileModal"
			:doc
			:docs-to-pick="docsToPick"
			:linked-doc="linkedDoc"
			@picked="existingFilePicked"
			@dropped="updateFile"
		/>
	</li>
</template>

<script setup lang="ts">
import type {
	DossierDocument,
	HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import { getFileIcon, getFileType } from "@/helpers/fileHelpers";
import { splitHdnValue } from "@/helpers/hdnHelpers";
import { useDropZone } from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";

const props = defineProps<{
	doc: HdnRequestedDocument;
	docsToPick: DossierDocument[];
}>();

const emit = defineEmits<{
	dropped: [files: [File] | []];
	picked: [doc: DossierDocument];
}>();

const fileBlob = ref<Blob | null>(null);
const showSelectFileModal = ref(false);

const documentUploadListItem = useTemplateRef("document-upload-list-item");
const { isOverDropZone } = useDropZone(documentUploadListItem, {
	multiple: false,
	onDrop,
});

const linkedDoc = computed(() =>
	props.docsToPick.find(
		(d) => d.id === props.doc.linkedDocumentAction?.documentId,
	),
);

function onDrop(files: File[] | null) {
	updateFile(files?.[0] ?? null);
}

function updateFile(file: File | null) {
	fileBlob.value = file;

	emit("dropped", file ? [file] : []);
}

function existingFilePicked(doc: DossierDocument) {
	emit("picked", doc);
}
</script>
